<script lang="ts">
 /**
 * Post-capture prompt asking whether to set the new container as active.
 *
 * @remarks
 * Shown after confirming a container-mode capture. The AI has identified
 * the captured item as a container (e.g. a toolbox, shelf, room). This
 * drawer asks whether to switch the active capture context to that
 * newly created container.
 *
 * **Two choices:**
 * - **Done** — keep the current container, return to idle
 * - **Set as container** — switch capture context to the new container,
 *   then return to idle in items mode (now cataloging into the new container)
 */
</script>

<script setup lang="ts">
defineProps<{
  /** Whether the drawer is open (bound to flow.isContainerPrompt). */
  open: boolean
  /** Display name of the newly created container. */
  containerName: string
}>()

defineEmits<{
  /** Fired when the drawer is dragged closed. Resets the flow. */
  "update:open": [value: boolean]
  /** Fired when "Set as container" is tapped. Switches active container context. */
  setContainer: []
  /** Fired when "Done" is tapped. Keeps current container, returns to idle. */
  done: []
}>()
</script>

<template>
  <UDrawer :open="open" @update:open="$emit('update:open', $event)">
    <template #content>
      <div class="flex flex-col items-center gap-4 p-6">
        <UIcon name="i-lucide-box" class="text-primary size-8" />
        <p class="text-center text-sm font-medium">Saved "{{ containerName }}" as container</p>
        <p class="text-muted text-center text-xs">Set it as the active container for capture?</p>
        <div class="flex w-full flex-col gap-2">
          <UButton block size="xl" @click="$emit('setContainer')">Set as container</UButton>
          <UButton block size="xl" variant="ghost" color="neutral" @click="$emit('done')">
            Done
          </UButton>
        </div>
      </div>
    </template>
  </UDrawer>
</template>
